import { useMemo } from "react";
import type { GasSpotSeriesPayload } from "../lib/api";
import { ChartCard } from "./ChartCard";
import { LineChart } from "./LineChart";
import type { Series } from "../lib/chart";

interface Props {
  data: GasSpotSeriesPayload;
}

const COLORS = {
  egsi: "#f59e0b", // amber-500
  forward: "#a78bfa", // violet-400
};

/**
 * THE Day-Ahead EGSI 7-day actuals + PEGAS THEE D0…D7 financial-tagesfutures
 * forward strip. D0 anchors to today and the strip extends a week into the
 * future as a dashed projection. EGSI tagesfutures lose liquidity beyond a
 * few days, so we annotate the back end of the curve.
 */
export function GasSpotChart({ data }: Props) {
  const today = useMemo(() => isoDateBerlin(new Date()), []);

  const series: Series[] = useMemo(() => {
    const list: Series[] = [];
    if (data.egsi.length > 0) {
      list.push({
        id: "egsi",
        label: "THE EGSI Day-Ahead",
        color: COLORS.egsi,
        points: data.egsi.map((p) => ({ x: p.date, y: p.settlement })),
      });
    }
    if (data.forwards.length > 0) {
      // Pin each Dn quote to (today + n) so the strip extends forward from now.
      // Settlement is only published once the day closes (~17:00 CET). Before
      // that, Montel returns only PrevSettlement — falling back to it keeps
      // the curve visible during the trading day rather than rendering an
      // empty line until the daily settle posts.
      const fwdPoints = data.forwards.map((f, i) => {
        const offset = parseInt(f.code.slice(1), 10);
        const dateOffset = Number.isFinite(offset) ? offset : i;
        const y = f.settlement ?? f.prevSettlement;
        return { x: addDaysIso(today, dateOffset), y };
      });
      // To make the join with EGSI visually obvious, prepend the last EGSI
      // point as a non-dashed seed if available.
      const last = data.egsi[data.egsi.length - 1];
      if (last) fwdPoints.unshift({ x: last.date, y: last.settlement });
      list.push({
        id: "forwards",
        label: "PEGAS THEE D0–D7",
        color: COLORS.forward,
        dashed: true,
        points: fwdPoints,
      });
    }
    return list;
  }, [data, today]);

  // Only flag genuine gaps — i.e. days where neither today's Settlement nor
  // yesterday's PrevSettlement is available. Missing-but-PrevSettlement-only
  // is the norm before the daily fix posts and should not raise a warning.
  const hasBackEndGap = data.forwards.some(
    (f, i) => i >= 4 && f.settlement === null && f.prevSettlement === null,
  );

  return (
    <ChartCard
      title="Spot & EGSI-Prognose"
      unit="EUR/MWh"
      accent="amber"
      updatedAt={data.updatedAt}
      footer={
        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
          EGSI-Werte sind nach Liefertag (Gastag) dargestellt — der zugehörige
          Handelstag ist jeweils der Vortag. Der Fix für einen Liefertag wird am
          Vortag nach Settlement (~17:00 CET) veröffentlicht.
          {" "}EGSI-Tagesfutures verlieren mit dem Horizont an Liquidität — D5–D7
          sind indikativ.
          {hasBackEndGap && " Einige hintere Tage sind aktuell ohne Settlement."}
        </p>
      }
    >
      {hasAnyError(data) && (
        <div className="mb-2 text-[11px] text-rose-700 dark:text-rose-300">
          Unvollständige Daten:{" "}
          {Object.entries(data.errors)
            .map(([k, v]) => `${k}: ${v}`)
            .join(" · ")}
        </div>
      )}
      <LineChart series={series} unit="€/MWh" nowMarker={data.now} height={280} />
    </ChartCard>
  );
}

function hasAnyError(d: GasSpotSeriesPayload): boolean {
  return Object.keys(d.errors).length > 0;
}

function isoDateBerlin(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
